import { useState } from "react";
import dayjs from "dayjs";

import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Popover from "react-bootstrap/Popover";
import Table from "react-bootstrap/Table";
import { FaEllipsisV, FaEye, FaInfoCircle } from "react-icons/fa";

import { formatIdentifier } from "@/utils/formatIdentifier";
import { handleColumnToggle } from "@/utils/handleColumnTogle";
import { getAge } from "@/utils/dateUtil";
import { DetailedCitizenAcsVisitsIndicatorC2Data } from "@/services/esus/indicator/esf/c2/getDetailedCitizensAcsVisitsIndicatorC2";
import { getCitizenScoreStatusDescriptionIndicatorC2 } from "@/utils/indicatorsEsf";

import { TableSkeletons } from "@/components/TableSkeletons";
import { CardOverlayTrigger } from "@/components/CardOverlayTrigger";

type Column = {
	title: string;
	visible: boolean;
};

type IndicatorEsfC2CitizensAcsVisitsTableColumns = {
	citizenName: Column;
	citizenAge: Column;
	document: Column;
	motherName: Column;
	healthTeam: Column;
	microarea: Column;
	firstAcsVisitDate: Column;
	secondAcsVisitDate: Column;
};

type IndicatorEsfC2CitizensAcsVisitsTableProps = {
	citizensAcsVisits: DetailedCitizenAcsVisitsIndicatorC2Data[];
	isLoading: boolean;
	openCitizenHistory: (citizenWithHomeVisits: DetailedCitizenAcsVisitsIndicatorC2Data) => void;
};

export function IndicatorEsfC2CitizensAcsVisitsTable({
	citizensAcsVisits,
	isLoading,
	openCitizenHistory,
}: Readonly<IndicatorEsfC2CitizensAcsVisitsTableProps>) {
	const [columns, setColumns] = useState<IndicatorEsfC2CitizensAcsVisitsTableColumns>({
		citizenName: { title: "Nome do cidadão", visible: true },
		citizenAge: { title: "Idade", visible: true },
		document: { title: "Documento", visible: true },
		motherName: { title: "Nome da mãe", visible: true },
		healthTeam: { title: "Equipe", visible: false },
		microarea: { title: "MA", visible: false },
		firstAcsVisitDate: { title: "1ª Visita", visible: true },
		secondAcsVisitDate: { title: "2ª Visita", visible: true },
	});

	return (
		<Table striped bordered hover responsive>
			<thead className="text-center fs-7">
				<tr className="fw-bolder text-center">
					<th className={`text-start px-2 ${!columns.citizenName.visible && "d-none"}`}>
						{columns.citizenName.title}
					</th>
					<th className={`px-2 ${!columns.citizenAge.visible && "d-none"}`}>
						{columns.citizenAge.title}
					</th>
					<th className={`px-2 ${!columns.document.visible && "d-none"}`}>
						{columns.document.title}
					</th>
					<th className={`px-2 ${!columns.motherName.visible && "d-none"}`}>
						{columns.motherName.title}
					</th>
					<th className={`px-2 ${!columns.healthTeam.visible && "d-none"}`}>
						{columns.healthTeam.title}
					</th>
					<th className={`px-2 ${!columns.microarea.visible && "d-none"}`}>
						{columns.microarea.title}
					</th>
					<th className={`px-2 ${!columns.firstAcsVisitDate.visible && "d-none"}`}>
						<div className="d-flex flex-column">
							{columns.firstAcsVisitDate.title}
							<CardOverlayTrigger
								header="1ª Visita"
								message="1ª Visita realizada por ACS ou TACS com motivo de visita preenchido como 'Acompanhamento de recém-nascido' até 30 dias de vida"
								icon={<FaInfoCircle className="text-muted" />}
							/>
						</div>
					</th>
					<th className={`px-2 ${!columns.secondAcsVisitDate.visible && "d-none"}`}>
						<div className="d-flex flex-column">
							{columns.secondAcsVisitDate.title}
							<CardOverlayTrigger
								header="2ª Visita"
								message="2ª Visita realizada por ACS ou TACS com a informação de motivo da visita preenchida como 'Acompanhamento de recém-nascido' até 6 meses de vida"
								icon={<FaInfoCircle className="text-muted" />}
							/>
						</div>
					</th>
					<th className="text-nowrap pb-0">
						Ações
						<OverlayTrigger
							trigger="click"
							placement="bottom"
							overlay={
								<Popover>
									<Popover.Body>
										<Form className="d-flex flex-column gap-2">
											{Object.keys(columns).map((key, index) => (
												<Form.Check
													key={`citizen-attendances-table-column-${
														index + 1
													}`}
													type="checkbox"
													label={
														columns[
															key as keyof IndicatorEsfC2CitizensAcsVisitsTableColumns
														].title
													}
													checked={
														columns[
															key as keyof IndicatorEsfC2CitizensAcsVisitsTableColumns
														].visible
													}
													onChange={() =>
														handleColumnToggle(
															key as keyof IndicatorEsfC2CitizensAcsVisitsTableColumns,
															setColumns
														)
													}
												/>
											))}
										</Form>
									</Popover.Body>
								</Popover>
							}
							rootClose
						>
							<Button
								title="Exibição de colunas da tabela"
								variant="white"
								className="px-2"
								disabled={isLoading}
							>
								<FaEllipsisV />
							</Button>
						</OverlayTrigger>
					</th>
				</tr>
			</thead>
			<tbody className="fs-8">
				{!isLoading && citizensAcsVisits.length > 0
					? citizensAcsVisits.map((citizenWithAcsVisits, index) => (
							<tr key={`citizen-attendances-table-row-${index + 1}`}>
								<td className={`px-2 ${!columns.citizenName.visible && "d-none"}`}>
									<div className="d-flex flex-column">
										<div>{citizenWithAcsVisits.fullName}</div>
										<div className="text-muted">
											{citizenWithAcsVisits.birthdate
												? dayjs(citizenWithAcsVisits.birthdate).format(
														"DD/MM/YYYY"
												  )
												: ""}
										</div>
									</div>
								</td>
								<td
									className={`text-center text-nowrap px-2 ${
										!columns.citizenAge.visible && "d-none"
									}`}
								>
									{getAge(citizenWithAcsVisits.birthdate)}
								</td>
								<td
									className={`text-center text-nowrap px-2 ${
										!columns.document.visible && "d-none"
									}`}
								>
									{citizenWithAcsVisits.cpf
										? formatIdentifier(citizenWithAcsVisits.cpf)
										: citizenWithAcsVisits.cns ?? ""}
								</td>
								<td
									className={`text-center px-2 ${
										!columns.motherName.visible && "d-none"
									}`}
								>
									{citizenWithAcsVisits.motherName ?? ""}
								</td>
								<td className={`px-2 ${!columns.healthTeam.visible && "d-none"}`}>
									<div className="d-flex flex-column">
										<div>{citizenWithAcsVisits.healthTeam?.name ?? ""}</div>
										<div className="text-muted">
											{citizenWithAcsVisits.healthTeam?.ine ?? ""}
										</div>
									</div>
								</td>
								<td
									className={`text-center text-nowrap px-2 ${
										!columns.microarea.visible && "d-none"
									}`}
								>
									{citizenWithAcsVisits.area ?? "FA"}
								</td>
								<td
									className={`text-center px-2 ${
										!columns.firstAcsVisitDate.visible && "d-none"
									}`}
								>
									<div className="d-flex flex-column">
										<div>
											{getCitizenScoreStatusDescriptionIndicatorC2(
												citizenWithAcsVisits.firstAcsHomeVisitStatus
											)}
										</div>
										<div className="text-muted">
											{citizenWithAcsVisits.firstAcsHomeVisit?.date
												? dayjs(
														citizenWithAcsVisits.firstAcsHomeVisit.date
												  ).format("DD/MM/YYYY")
												: ""}
										</div>
									</div>
								</td>
								<td
									className={`text-center px-2 ${
										!columns.secondAcsVisitDate.visible && "d-none"
									}`}
								>
									<div className="d-flex flex-column">
										<div>
											{getCitizenScoreStatusDescriptionIndicatorC2(
												citizenWithAcsVisits.secondAcsHomeVisitStatus
											)}
										</div>
										<div className="text-muted">
											{citizenWithAcsVisits.secondAcsHomeVisit?.date
												? dayjs(
														citizenWithAcsVisits.secondAcsHomeVisit.date
												  ).format("DD/MM/YYYY")
												: ""}
										</div>
									</div>
								</td>
								<td className="text-center">
									<Button
										className="bg-primary py-1 px-3"
										onClick={() => openCitizenHistory(citizenWithAcsVisits)}
										title="Histórico de visitas do ACS"
									>
										<FaEye className="fs-4" />
									</Button>
								</td>
							</tr>
					  ))
					: isLoading && (
							<TableSkeletons
								numberOfCells={
									Object.values(columns).reduce(
										(count, column) =>
											count + (column?.visible === true ? 1 : 0),
										0
									) + 1
								}
							/>
					  )}
			</tbody>
		</Table>
	);
}
